// Graphique du kilometrage prevu par semaine du decompte en cours (ou du
// prochain). Le realise ne s'affiche qu'a la demande, en repere discret :
// ni pourcentage ni couleur d'alerte, pour ne pas en faire un objectif.
import * as React from 'react';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { lireJournal, type ActiviteRealisee } from '../Journal/journalApi.ts';
import { kilometragesParSemaine, km, type KilometrageSemaine } from './kilometrageDecompte.ts';
import { ajouterJours, iso } from './dates.ts';
import { dateLocale } from './decompte.ts';
import { lirePeriode } from './exportPlan.ts';
import { infoSemaine, type Cible, type Decompte, type SeancePlanifiee } from './planningApi.ts';

// Choix "Afficher le realise", memorise dans le navigateur (masque par defaut).
const CLE_REALISE = 'calendrier.decompte.realise';
// Periode lue par appel au journal (le serveur limite a 62 jours).
const JOURS_PAR_APPEL = 56;
const HAUTEUR = 110;

function lireRealise(): boolean {
  try {
    return localStorage.getItem(CLE_REALISE) === '1';
  } catch {
    return false;
  }
}

function sauverRealise(v: boolean): void {
  try {
    localStorage.setItem(CLE_REALISE, v ? '1' : '0');
  } catch {
    // stockage indisponible : le choix n'est pas memorise
  }
}

/** Activites du decompte jusqu'a aujourd'hui, par tranches. */
async function lireActivites(d: Decompte, aujourdhui: string, athlete: Cible): Promise<ActiviteRealisee[]> {
  const finDecompte = iso(ajouterJours(dateLocale(d.lundiCible), 6));
  const fin = aujourdhui < finDecompte ? aujourdhui : finDecompte;
  const activites: ActiviteRealisee[] = [];
  for (let debut = dateLocale(d.lundiDebut); iso(debut) <= fin; debut = ajouterJours(debut, JOURS_PAR_APPEL)) {
    const finTranche = iso(ajouterJours(debut, JOURS_PAR_APPEL - 1));
    const journal = await lireJournal(iso(debut), finTranche < fin ? finTranche : fin, false, athlete);
    activites.push(...journal.activites);
  }
  return activites;
}

const dateCourte = (lundi: string) => dateLocale(lundi).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

type Props = {
  decompte: Decompte;
  athlete: Cible;
  // Jour courant de l'athlete (AAAA-MM-JJ) et lundi de sa semaine.
  aujourdhui: string;
  lundiCourant: string;
  // Seances des jours affiches (debut -> fin) : elles remplacent celles lues
  // pour ces jours, et le graphique suit ainsi ajouts, deplacements et retraits.
  planning: SeancePlanifiee[];
  debut: string;
  fin: string;
  // Types de semaine connus (assimilation, affutage), completes par la lecture du decompte.
  semaines: Map<string, string>;
};

export default function GraphiqueKilometrageDecompte({ decompte, athlete, aujourdhui, lundiCourant, planning, debut, fin, semaines }: Props) {
  const [seances, setSeances] = React.useState<SeancePlanifiee[]>([]);
  const [types, setTypes] = React.useState<Map<string, string>>(new Map());
  const [erreur, setErreur] = React.useState<string | null>(null);
  const [afficherRealise, setAfficherRealise] = React.useState<boolean>(lireRealise);
  const [activites, setActivites] = React.useState<ActiviteRealisee[] | null>(null);

  React.useEffect(() => {
    let actif = true;
    lirePeriode(decompte, athlete)
      .then((r) => {
        if (!actif) return;
        setSeances(r.seances);
        setTypes(r.types);
        setErreur(null);
      })
      .catch((e: Error) => actif && setErreur(e.message));
    return () => { actif = false; };
  }, [decompte, athlete]);

  React.useEffect(() => {
    setActivites(null);
    if (!afficherRealise) return;
    let actif = true;
    lireActivites(decompte, aujourdhui, athlete)
      .then((a) => actif && setActivites(a))
      .catch(() => actif && setActivites([]));
    return () => { actif = false; };
  }, [afficherRealise, decompte, aujourdhui, athlete]);

  const semainesKm = React.useMemo(
    () => kilometragesParSemaine(
      decompte,
      [...seances.filter((p) => p.date < debut || p.date > fin), ...planning],
      afficherRealise ? activites : null,
      aujourdhui,
    ),
    [decompte, seances, planning, debut, fin, activites, afficherRealise, aujourdhui],
  );
  const max = Math.max(1, ...semainesKm.map((c) => Math.max(c.prevu, c.realise ?? 0)));
  const total = semainesKm.reduce((t, c) => t + c.prevu, 0);
  const enCours = decompte.lundiDebut <= lundiCourant;

  const basculer = (v: boolean) => {
    setAfficherRealise(v);
    sauverRealise(v);
  };

  // Type de semaine : celui du calendrier pour les semaines affichees (a jour
  // apres un changement), sinon celui lu pour le decompte.
  const typeDe = (lundi: string) => infoSemaine(debut <= lundi && lundi <= fin ? semaines.get(lundi) : types.get(lundi));

  const infobulle = (c: KilometrageSemaine) => {
    const type = typeDe(c.lundi);
    return (
      <>
        <b>S{c.n}</b> · semaine du {dateCourte(c.lundi)}{type ? ` (${type.libelle.toLowerCase()})` : ''}
        <br />Prévu : {km(c.prevu)} km
        {c.realise != null && <><br />Réalisé : {km(c.realise)} km</>}
      </>
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 2, mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Kilométrage prévu du décompte{enCours ? ' en cours' : ' à venir'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          S{semainesKm.length} au {dateCourte(decompte.lundiDebut)} → S1 au {dateCourte(decompte.lundiCible)} · {km(total)} km prévus
        </Typography>
        <Tooltip title="Kilomètres courus d'après intervals.icu : un repère pour comprendre la fatigue, pas un objectif à atteindre.">
          <FormControlLabel
            sx={{ ml: 'auto', mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
            control={<Switch size="small" checked={afficherRealise} onChange={(e) => basculer(e.target.checked)} />}
            label="Afficher le réalisé"
          />
        </Tooltip>
      </Box>
      {erreur ? (
        <Typography variant="body2" color="error">Kilométrage indisponible : {erreur}</Typography>
      ) : (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end', overflowX: 'auto', pb: 0.5 }}>
          {semainesKm.map((c) => {
            const type = typeDe(c.lundi);
            const courante = c.lundi === lundiCourant;
            const passee = c.lundi < lundiCourant;
            return (
              <Tooltip key={c.lundi} title={infobulle(c)}>
                <Box sx={{ flex: '1 0 28px', minWidth: 28, maxWidth: 72, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box sx={{ fontSize: '0.7rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums', mb: 0.25 }}>
                    {c.prevu >= 500 ? km(c.prevu) : ''}
                  </Box>
                  <Box sx={{ position: 'relative', width: '100%', height: HAUTEUR, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                    <Box
                      sx={{
                        width: '70%',
                        height: `${(c.prevu / max) * 100}%`,
                        minHeight: c.prevu > 0 ? 2 : 0,
                        bgcolor: type?.couleur ?? 'primary.main',
                        opacity: passee ? 0.45 : 0.85,
                        borderRadius: '3px 3px 0 0',
                      }}
                    />
                    {/* Realise : simple trait gris, sans jugement */}
                    {c.realise != null && c.realise > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '8%',
                          right: '8%',
                          bottom: `${(c.realise / max) * 100}%`,
                          borderTop: '2px dashed',
                          borderColor: 'text.secondary',
                        }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      mt: 0.5,
                      px: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: courante ? 700 : 400,
                      color: courante ? 'text.primary' : 'text.secondary',
                      borderBottom: courante ? 2 : 0,
                      borderColor: 'primary.main',
                    }}
                  >
                    S{c.n}
                  </Box>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      )}
      {afficherRealise && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {activites == null
            ? 'Lecture des activités…'
            : 'Trait gris : kilomètres courus (sorties de course intervals.icu), à titre indicatif.'}
        </Typography>
      )}
    </Paper>
  );
}
